import { useState } from 'react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import {
  UsersThree,
  UserCheck,
  CheckCircle,
  Heart,
  Check,
  Plus,
  CaretDown,
  ChartLineUp,
  Briefcase,
  Monitor,
  Leaf,
  Heartbeat,
  GraduationCap,
  Sparkle,
  ForkKnife,
  AirplaneTilt,
  TShirt,
  Barbell,
  GameController,
  FilmSlate,
  BookOpen,
  MusicNote,
  Trophy,
  Baby,
  House,
  Car,
  Tag,
  type Icon,
} from '../design-system/icons';
import type { UseFormRegister, UseFormSetValue, FieldErrors } from 'react-hook-form';
import type { CampaignFormData } from './campaignSchema';
import type { AudienceDropdownKey } from './types';
import { CREATOR_CATEGORIES, TIERS, LANGUAGE_OPTIONS, REGIONS, GENDER_OPTIONS, err, invalidBorder } from './constants';

interface StepTargetingProps {
  register: UseFormRegister<CampaignFormData>;
  setValue: UseFormSetValue<CampaignFormData>;
  errors: FieldErrors<CampaignFormData>;
  totalCreators: number;
  ageMin: number | undefined;
  ageMax: number | undefined;
  gender: string[];
  language: string[];
  region: string[];
  creatorGenders: string[];
  creatorCategories: string[];
  tierPresets: string[];
  toggleCategory: (cat: string) => void;
  addCustomCategory: () => void;
  toggleTier: (tierValue: string) => void;
  toggleMulti: (field: AudienceDropdownKey, value: string) => void;
  setMultiValues: (field: AudienceDropdownKey, values: string[]) => void;
  customCategoryInput: string;
  setCustomCategoryInput: (v: string) => void;
}

const AGE_FLOOR = 13;
const AGE_CEIL = 65;
// Figma: each step is stacked cards — a header card, then one card per section.
const CARD = 'rounded-xl border border-border bg-card px-8 py-8';

const CATEGORY_ICONS: Record<string, Icon> = {
  Fashion: TShirt, Beauty: Sparkle, Lifestyle: Leaf, Food: ForkKnife, Travel: AirplaneTilt,
  Tech: Monitor, Fitness: Barbell, Gaming: GameController, Education: GraduationCap,
  Entertainment: FilmSlate, Finance: ChartLineUp, Learning: BookOpen, 'Health & Wellness': Heartbeat,
  Music: MusicNote, Sports: Trophy, Parenting: Baby, 'Home & Decor': House, Automotive: Car,
  Business: Briefcase, Health: Heart,
};

function PurpleCheck({ size = 16 }: { size?: number }) {
  return (
    <span
      className="flex items-center justify-center rounded-full bg-[var(--brand-500)] text-white"
      style={{ width: size, height: size }}
    >
      <Check size={size - 5} weight="bold" />
    </span>
  );
}

// Selected = black outline only (no fill, no checkmark) — matches Figma's
// pill spec app-wide for multi/binary selections.
function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-[34px] items-center rounded-full border bg-card px-[13px] text-body-sm-medium transition-colors ${active ? 'border-foreground text-foreground' : 'border-border text-foreground hover:border-foreground/30'}`}
    >
      {children}
    </button>
  );
}

// Pill toggle group with an "All" option (All = empty filter).
function PillGroup({ value, options, onSet, onToggle }: {
  value: string[];
  options: string[];
  onSet: (v: string[]) => void;
  onToggle: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      <Pill active={value.length === 0} onClick={() => onSet([])}>All</Pill>
      {options.map((o) => (
        <Pill key={o} active={value.includes(o)} onClick={() => onToggle(o)}>{o}</Pill>
      ))}
    </div>
  );
}

// Multi-select dropdown (Figma "Select region" / "Select language"). Options
// come from the predefined lists; trigger shows the chosen values or placeholder.
function MultiSelectCombobox({ placeholder, options, value, onChange }: {
  placeholder: string;
  options: readonly string[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const toggle = (o: string) =>
    onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 text-left transition-colors hover:border-foreground/30"
        >
          <span className={`truncate text-body-sm-medium ${value.length ? 'text-foreground' : 'text-muted-foreground'}`}>
            {value.length ? value.join(', ') : placeholder}
          </span>
          <CaretDown size={15} className="shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-1">
        {options.map((o) => {
          const active = value.includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => toggle(o)}
              className="flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-2 text-left text-body-sm-regular text-foreground transition-colors hover:bg-muted"
            >
              <span className="truncate">{o}</span>
              {active && <Check size={15} weight="bold" className="shrink-0 text-foreground" />}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

function SectionHead({ icon: I, title, note, sub }: { icon: Icon; title: string; note: string; sub: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="flex items-center gap-2 text-body-md-medium text-foreground">
        <I size={14} className="text-foreground-secondary-default" /> {title}
        <span className="text-caption-regular text-muted-foreground">· {note}</span>
      </span>
      <span className="text-caption-regular text-muted-foreground">{sub}</span>
    </div>
  );
}

export function StepTargeting({
  register, setValue, errors, totalCreators, ageMin, ageMax,
  gender, language, region, creatorGenders, creatorCategories, tierPresets,
  toggleCategory, addCustomCategory, toggleTier, toggleMulti, setMultiValues,
  customCategoryInput, setCustomCategoryInput,
}: StepTargetingProps) {
  const [showAddCat, setShowAddCat] = useState(false);
  void totalCreators;
  void ageMin;
  void ageMax;
  void setValue;

  const commitCustomCat = () => {
    if (customCategoryInput.trim()) addCustomCategory();
    setShowAddCat(false);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Header card */}
      <div className={`${CARD} flex items-start gap-3.5`}>
        <span className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-muted">
          <CheckCircle size={20} className="text-foreground" />
        </span>
        <div>
          <h2 className="m-0 text-heading-h5 text-foreground">Creator &amp; audience targeting</h2>
          <p className="m-0 text-body-sm-regular text-muted-foreground">
            Define your target audience and the kind of creators you want.
          </p>
        </div>
      </div>

      {/* ── Target audience card (Figma: 2-col grid) ── */}
      <div className={`${CARD} flex flex-col gap-4`}>
        <SectionHead icon={UserCheck} title="Target audience" note="who sees the content" sub="All optional — leave empty for no filter." />

        <div className="grid grid-cols-2 gap-x-6 gap-y-5">
          {/* Age — min/max inputs (Figma) */}
          <div className="flex flex-col gap-2">
            <span className="text-body-sm-medium text-secondary-foreground">Age</span>
            <div className="flex items-center gap-3">
              <Input type="number" min={AGE_FLOOR} max={AGE_CEIL} placeholder="18" className={`h-10 ${invalidBorder(!!errors.ageMin)}`}
                aria-invalid={!!errors.ageMin} {...register('ageMin', { valueAsNumber: true })} />
              <span className="text-body-sm-medium text-muted-foreground">To</span>
              <Input type="number" min={AGE_FLOOR} max={AGE_CEIL} placeholder="34" className={`h-10 ${invalidBorder(!!errors.ageMax)}`}
                aria-invalid={!!errors.ageMax} {...register('ageMax', { valueAsNumber: true })} />
            </div>
            {(errors.ageMin || errors.ageMax) && (
              <span className={err}>{errors.ageMin?.message ?? errors.ageMax?.message}</span>
            )}
          </div>

          {/* Gender */}
          <div className="flex flex-col gap-2">
            <span className="text-body-sm-medium text-secondary-foreground">Gender</span>
            <PillGroup value={gender} options={GENDER_OPTIONS}
              onSet={(v) => setMultiValues('gender', v)} onToggle={(v) => toggleMulti('gender', v)} />
            {errors.gender && <span className={err}>{errors.gender.message}</span>}
          </div>

          {/* Region — multi-select dropdown (Figma) */}
          <div className="flex flex-col gap-2">
            <span className="text-body-sm-medium text-secondary-foreground">Region</span>
            <MultiSelectCombobox
              placeholder="Select region"
              options={REGIONS}
              value={region}
              onChange={(v) => setMultiValues('region', v)}
            />
          </div>

          {/* Language — multi-select dropdown (Figma) */}
          <div className="flex flex-col gap-2">
            <span className="text-body-sm-medium text-secondary-foreground">Language</span>
            <MultiSelectCombobox
              placeholder="Select language"
              options={LANGUAGE_OPTIONS}
              value={language}
              onChange={(v) => setMultiValues('language', v)}
            />
          </div>
        </div>
      </div>

      {/* ── Target creators card ── */}
      <div className={`${CARD} flex flex-col gap-6`}>
        <SectionHead icon={UsersThree} title="Target creators" note="who makes the content" sub="The kind of creators Discovr should match." />

        {/* No. of creators (Figma: first field in Target creators; BE-required totalCreators) */}
        <div className="flex flex-col gap-2">
          <span className="flex items-center gap-1 text-body-sm-medium text-secondary-foreground">No. of creators<span className="text-foreground-destructive-default">*</span></span>
          <Input type="number" min={1} max={2000} placeholder="e.g. 10" className={`h-10 max-w-[200px] ${invalidBorder(!!errors.totalCreators)}`} aria-invalid={!!errors.totalCreators} {...register('totalCreators')} />
          {errors.totalCreators ? (
            <span className={err}>{errors.totalCreators.message}</span>
          ) : (
            <span className="text-caption-regular text-muted-foreground">Between 1 and 2000 creators.</span>
          )}
        </div>

        {/* Creator gender */}
        <div className="flex flex-col gap-2">
          <span className="text-body-sm-medium text-secondary-foreground">Creator gender <span className="font-normal text-muted-foreground/70">optional</span></span>
          <PillGroup value={creatorGenders} options={GENDER_OPTIONS}
            onSet={(v) => setMultiValues('creatorGenders', v)} onToggle={(v) => toggleMulti('creatorGenders', v)} />
          {errors.creatorGenders && <span className={err}>{errors.creatorGenders.message}</span>}
        </div>

        {/* Notes for our team */}
        <div className="flex flex-col gap-[6px]">
          <span className="text-body-sm-medium text-secondary-foreground">Notes for our team <span className="font-normal text-muted-foreground/70">optional</span></span>
          <Input placeholder="e.g. open to all but lean female" className={`h-10 ${invalidBorder(!!errors.creatorGenderNotes)}`} aria-invalid={!!errors.creatorGenderNotes} {...register('creatorGenderNotes')} />
          {errors.creatorGenderNotes && <span className={err}>{errors.creatorGenderNotes.message}</span>}
        </div>

        {/* Creator categories */}
        <div className="flex flex-col gap-2">
          <span className="flex items-center gap-1 text-caption-medium text-foreground">Creator categories<span className="text-foreground-destructive-default">*</span></span>
          <div className="flex flex-wrap gap-3">
            {creatorCategories
              .filter((c) => !CREATOR_CATEGORIES.includes(c))
              .map((cat) => (
                <button key={cat} type="button" onClick={() => toggleCategory(cat)}
                  className="flex h-[56px] items-center gap-4 rounded-xl border-[1.5px] border-foreground bg-card px-3 text-body-sm-medium">
                  <span className="flex size-8 items-center justify-center rounded-[9px] bg-muted"><Tag size={16} className="text-foreground" /></span>
                  <span className="text-foreground">{cat}</span>
                  <PurpleCheck />
                </button>
              ))}
            {CREATOR_CATEGORIES.map((cat) => {
              const CatIcon = CATEGORY_ICONS[cat] ?? Tag;
              const active = creatorCategories.includes(cat);
              return (
                <button key={cat} type="button" onClick={() => toggleCategory(cat)}
                  className={`flex h-[56px] items-center gap-4 rounded-xl border-[1.5px] bg-card px-3 text-body-sm-medium transition-colors ${active ? 'border-foreground' : 'border-border hover:border-foreground/30'}`}>
                  <span className="flex size-8 items-center justify-center rounded-[9px] bg-muted"><CatIcon size={16} className="text-foreground" /></span>
                  <span className="text-foreground">{cat}</span>
                  {/* Always reserve the check's slot (border width is also constant at 1.5px) so
                      selecting a category never changes the button width — no layout shift. */}
                  <span className={active ? '' : 'invisible'}><PurpleCheck /></span>
                </button>
              );
            })}
            {/* Same anatomy as the category chips (h-[56px], rounded-xl, icon
                box + label) so it reads as part of the same row instead of a
                visually distinct control — dashed border + muted tone is the
                only differentiator, marking it as "add" rather than "select". */}
            <button type="button" onClick={() => setShowAddCat(true)}
              className="flex h-[56px] items-center gap-4 rounded-xl border-[1.5px] border-dashed border-border bg-card px-3 text-body-sm-medium text-muted-foreground transition-colors hover:border-foreground/30">
              <span className="flex size-8 items-center justify-center rounded-[9px] bg-muted"><Plus size={16} className="text-foreground" /></span>
              <span>Add</span>
            </button>
          </div>
          {/* Custom category — popup (like the other Figma popups). */}
          <Dialog open={showAddCat} onOpenChange={(v) => { if (!v) setCustomCategoryInput(''); setShowAddCat(v); }}>
            <DialogContent className="sm:max-w-sm">
              <DialogHeader>
                <DialogTitle>Add a category</DialogTitle>
                <DialogDescription>Add a custom creator category to target.</DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-3 pt-2">
                <Input
                  autoFocus
                  value={customCategoryInput}
                  onChange={(e) => setCustomCategoryInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitCustomCat(); } }}
                  placeholder="e.g. SaaS, Crypto, Parenting"
                  className="h-10"
                />
                <div className="flex justify-end">
                  <Button onClick={commitCustomCat} disabled={!customCategoryInput.trim()}>Add category</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          {errors.creatorCategories && <span className={err}>{errors.creatorCategories.message}</span>}
        </div>

        {/* Creator tier presets (no availability counts — no BE) */}
        <div className="flex flex-col gap-2">
          <span className="flex flex-wrap items-center gap-2 text-caption-medium text-foreground">
            Creator tier presets<span className="text-foreground-destructive-default">*</span>
            <span className="font-normal text-muted-foreground/70">· select one or more · followers on selected platform</span>
          </span>
          <div className="grid grid-cols-3 gap-3">
            {TIERS.map((t) => {
              const active = tierPresets.includes(t.value);
              return (
                <button key={t.value} type="button" onClick={() => toggleTier(t.value)}
                  className={`relative flex flex-col gap-1 rounded-2xl border bg-card py-4 px-5 text-left transition-colors ${active ? 'border-foreground' : 'border-border hover:border-foreground/30'}`}>
                  <span className="absolute top-4 right-4">
                    {active ? <PurpleCheck size={28} /> : <span className="block size-7 rounded-full border border-border bg-muted" />}
                  </span>
                  <span className="text-body-md-medium text-foreground">{t.label}</span>
                  <span className="text-caption-regular text-muted-foreground">{t.desc}</span>
                </button>
              );
            })}
          </div>
          {errors.tierPresets && <span className={err}>{errors.tierPresets.message}</span>}
        </div>
      </div>
    </div>
  );
}
